/**
 * QuickAgent — Phase 3 §3.2
 * Floating Action Button (bottom-right, red+gold) + Cmd/Ctrl+K modal.
 * Global search across tickets, factures, devis, clients via /api/admin/search/global.
 *
 * Design: red (#c8102e), black (#0a0a0a), gold (#d4af37). Premium, no gradients
 * on white. Only mounted for admin/staff/technicien roles.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, X, FileText, Receipt, Wrench, User, Plus, Loader2, Command as CmdIcon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  ticket:  { icon: Wrench,   label: 'Ticket',   accent: '#c8102e' },
  facture: { icon: Receipt,  label: 'Facture',  accent: '#d4af37' },
  devis:   { icon: FileText, label: 'Devis',    accent: '#d4af37' },
  client:  { icon: User,     label: 'Client',   accent: '#9aa0a6' },
};

export default function QuickAgent() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [recents, setRecents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const cacheRef = useRef(new Map()); // {q: {ts, results}}

  // Mount only for admin/staff/technicien (not clients, not super_admin)
  const allowed = isAuthenticated && ['admin', 'user', 'technicien', 'staff'].includes(user?.role);

  // Cmd+K / Ctrl+K listener
  useEffect(() => {
    if (!allowed) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [allowed, open]);

  // Auto-open via ?quickagent=1 (PWA shortcut "Recherche rapide")
  useEffect(() => {
    if (!allowed) return undefined;
    if (typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(window.location.search);
    if (params.get('quickagent') === '1') {
      setOpen(true);
    }
    return undefined;
  }, [allowed]);

  // Focus input + load recents on open
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    if (recents.length === 0) {
      const token = localStorage.getItem('token');
      axios.get(`${API}/api/admin/search/recent`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => setRecents(r.data?.items || [])).catch(() => {});
    }
    return () => clearTimeout(t);
  }, [open, recents.length]);

  // Debounced search
  const runSearch = useCallback((term) => {
    if (!term.trim()) { setResults([]); return; }
    const cached = cacheRef.current.get(term);
    if (cached && Date.now() - cached.ts < 30000) {
      setResults(cached.results);
      return;
    }
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/admin/search/global`, {
      params: { q: term },
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      const items = r.data?.results || [];
      cacheRef.current.set(term, { ts: Date.now(), results: items });
      setResults(items);
      setHighlight(0);
    }).catch(() => setResults([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 200);
    return () => clearTimeout(debounceRef.current);
  }, [q, runSearch]);

  const items = q.trim() ? results : recents;

  // Keyboard nav inside list
  const onListKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && items[highlight]) {
      e.preventDefault();
      go(items[highlight].url);
    }
  };

  const go = (url) => {
    setOpen(false);
    setQ('');
    if (url) navigate(url);
  };

  if (!allowed) return null;

  return (
    <>
      {/* Floating Action Button */}
      <button
        type="button"
        data-testid="quickagent-fab"
        onClick={() => setOpen(true)}
        aria-label="Ouvrir l'agent de recherche (Ctrl+K)"
        className="qa-fab"
      >
        <Search size={22} strokeWidth={2.4} />
      </button>

      {open && (
        <div
          className="qa-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          onKeyDown={onListKey}
          tabIndex={-1}
        >
          <div className="qa-panel" data-testid="quickagent-panel">
            {/* Header / Input */}
            <div className="qa-header">
              <Search size={18} className="qa-header-icon" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher tickets, factures, devis, clients…"
                className="qa-input"
                data-testid="quickagent-input"
                autoComplete="off"
              />
              {loading && <Loader2 size={16} className="qa-spinner" />}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="qa-close"
                aria-label="Fermer"
                data-testid="quickagent-close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Quick actions */}
            <div className="qa-actions">
              <ActionBtn icon={Wrench}  label="Nouveau ticket"   onClick={() => go('/reparations/nouveau')} testid="quickagent-new-ticket" />
              <ActionBtn icon={Receipt} label="Nouvelle facture" onClick={() => go('/devis-factures?action=new-facture')} testid="quickagent-new-facture" />
              <ActionBtn icon={FileText} label="Nouveau devis"   onClick={() => go('/devis-factures?action=new-devis')} testid="quickagent-new-devis" />
            </div>

            {/* Results / Recents */}
            <div className="qa-section-title">
              {q.trim() ? 'Résultats' : 'Récents'}
              {!q.trim() && (
                <span className="qa-hint">
                  <kbd>↑</kbd><kbd>↓</kbd> naviguer · <kbd>↵</kbd> ouvrir · <kbd>Esc</kbd> fermer
                </span>
              )}
            </div>
            <div className="qa-list" data-testid="quickagent-list">
              {items.length === 0 && (
                <div className="qa-empty">
                  {q.trim() ? 'Aucun résultat.' : 'Aucun élément récent.'}
                </div>
              )}
              {items.map((it, i) => {
                const meta = TYPE_META[it.type] || TYPE_META.client;
                const Icon = meta.icon;
                return (
                  <button
                    type="button"
                    key={`${it.type}-${it.title}-${i}`}
                    onClick={() => go(it.url)}
                    onMouseEnter={() => setHighlight(i)}
                    className={`qa-row ${highlight === i ? 'qa-row-active' : ''}`}
                    data-testid={`quickagent-item-${i}`}
                  >
                    <span className="qa-row-icon" style={{ color: meta.accent }}>
                      <Icon size={16} strokeWidth={2.2} />
                    </span>
                    <span className="qa-row-body">
                      <span className="qa-row-title">{it.title}</span>
                      <span className="qa-row-sub">{it.subtitle}</span>
                    </span>
                    <span className="qa-row-tag" style={{ borderColor: meta.accent, color: meta.accent }}>
                      {meta.label}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="qa-footer">
              <span><CmdIcon size={12} /> + K pour rouvrir</span>
              <span>Remobiles · Quick Agent</span>
            </div>
          </div>
        </div>
      )}

      {/* Inline styles to avoid touching shared CSS */}
      <style>{`
        .qa-fab {
          position: fixed; bottom: 22px; right: 22px;
          width: 56px; height: 56px; border-radius: 999px;
          background: linear-gradient(145deg, #d11833 0%, #8a0a1d 100%);
          color: #fff;
          border: 1px solid #d4af37;
          box-shadow: 0 12px 28px rgba(200,16,46,0.45), 0 2px 0 rgba(212,175,55,0.4) inset;
          display: grid; place-items: center;
          cursor: pointer;
          z-index: 9990;
          transition: transform .18s ease, box-shadow .18s ease;
        }
        .qa-fab:hover { transform: translateY(-2px) scale(1.04); box-shadow: 0 18px 36px rgba(200,16,46,0.6); }
        .qa-fab:active { transform: scale(0.96); }

        .qa-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.65);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          z-index: 9995;
          display: grid; place-items: start center;
          padding-top: 10vh;
          animation: qaFade .15s ease;
        }
        @keyframes qaFade { from { opacity:0 } to { opacity:1 } }

        .qa-panel {
          width: min(640px, 92vw);
          background: linear-gradient(155deg, #141414 0%, #0a0a0a 100%);
          border: 1px solid rgba(212,175,55,0.22);
          border-radius: 16px;
          box-shadow: 0 32px 80px rgba(0,0,0,0.7);
          color: #f5f5f5;
          overflow: hidden;
          font-family: -apple-system, "SF Pro Display", "Segoe UI", system-ui, sans-serif;
          animation: qaPop .18s cubic-bezier(.2,.9,.3,1.2);
        }
        @keyframes qaPop { from { transform: translateY(-8px) scale(.98); opacity: 0 } to { transform: none; opacity: 1 } }

        .qa-header {
          display: flex; align-items: center; gap: 10px;
          padding: 14px 16px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          background: linear-gradient(180deg, rgba(200,16,46,0.06) 0%, transparent 100%);
        }
        .qa-header-icon { color: #d4af37; }
        .qa-input {
          flex: 1;
          background: transparent;
          border: none; outline: none;
          color: #fff;
          font-size: 16px;
          font-weight: 500;
          letter-spacing: 0.01em;
        }
        .qa-input::placeholder { color: #777; }
        .qa-spinner { color: #d4af37; animation: qaSpin .8s linear infinite; }
        @keyframes qaSpin { to { transform: rotate(360deg) } }
        .qa-close {
          background: transparent; border: 1px solid rgba(255,255,255,0.1);
          color: #aaa; border-radius: 8px;
          width: 28px; height: 28px; display: grid; place-items: center;
          cursor: pointer;
          transition: all .15s ease;
        }
        .qa-close:hover { color: #fff; border-color: #c8102e; background: rgba(200,16,46,0.1); }

        .qa-actions { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; padding: 12px; }
        .qa-action {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(212,175,55,0.18);
          color: #fff;
          border-radius: 10px;
          padding: 10px 8px;
          font-size: 12px; font-weight: 600;
          display: flex; flex-direction: column; align-items: center; gap: 6px;
          cursor: pointer;
          transition: all .15s ease;
        }
        .qa-action:hover {
          border-color: #d4af37;
          background: rgba(212,175,55,0.08);
          transform: translateY(-1px);
        }
        .qa-action svg { color: #c8102e; }

        .qa-section-title {
          padding: 10px 16px 6px;
          font-size: 11px; font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: #888;
          display: flex; align-items: center; justify-content: space-between;
        }
        .qa-hint { font-size: 10px; color: #555; font-weight: 500; letter-spacing: 0.04em; text-transform: none; }
        .qa-hint kbd {
          display: inline-block; padding: 1px 5px;
          background: #1a1a1a; border: 1px solid #333; border-radius: 4px;
          color: #aaa; font-size: 10px; margin: 0 2px;
        }

        .qa-list { max-height: 340px; overflow-y: auto; padding: 0 8px 8px; }
        .qa-empty { padding: 22px 16px; color: #666; text-align: center; font-size: 13px; }

        .qa-row {
          width: 100%;
          display: grid;
          grid-template-columns: 28px 1fr auto;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          background: transparent;
          border: 1px solid transparent;
          border-radius: 10px;
          color: #f5f5f5;
          cursor: pointer;
          text-align: left;
          transition: background .12s ease, border-color .12s ease;
        }
        .qa-row:hover, .qa-row-active {
          background: rgba(212,175,55,0.06);
          border-color: rgba(212,175,55,0.25);
        }
        .qa-row-icon { display: grid; place-items: center; }
        .qa-row-body { display: flex; flex-direction: column; min-width: 0; }
        .qa-row-title { font-weight: 600; font-size: 14px; color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .qa-row-sub   { font-size: 12px; color: #999; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .qa-row-tag {
          font-size: 10px; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase;
          border: 1px solid;
          padding: 2px 8px;
          border-radius: 999px;
          background: rgba(0,0,0,0.4);
        }

        .qa-footer {
          padding: 10px 16px;
          border-top: 1px solid rgba(255,255,255,0.06);
          display: flex; justify-content: space-between;
          font-size: 11px; color: #666;
          letter-spacing: 0.04em;
        }
        .qa-footer span { display: inline-flex; align-items: center; gap: 4px; }

        @media (max-width: 480px) {
          .qa-actions { grid-template-columns: 1fr; }
          .qa-overlay { padding-top: 4vh; }
          .qa-fab { bottom: 16px; right: 16px; }
        }
      `}</style>
    </>
  );
}

function ActionBtn({ icon: Icon, label, onClick, testid }) {
  return (
    <button type="button" className="qa-action" onClick={onClick} data-testid={testid}>
      <Icon size={18} />
      <span>{label}</span>
    </button>
  );
}
